import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import {
  Filter,
  Search,
  RotateCcw,
  PackageX,
  Camera,
  Send,
  Inbox,
  SearchX,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  X,
} from "lucide-react";
import { getAdminPackages, bulkFailedDelivery } from "api/packages";
import { getDeliveryConfig } from "api/settings";
import { showToast } from "utils/showToast";
import useAuth from "hooks/useAuth";
import { Card } from "components/ui/Card";
import { Button } from "components/ui/Button";
import { Badge } from "components/ui/Badge";
import { EmptyState } from "components/ui/EmptyState";
import { Skeleton } from "components/ui/Skeleton";
import { Modal } from "components/ui/Modal";
import { Table, THead, TBody, TR, TH, TD } from "components/ui/Table";
import { useIsMobile } from "hooks/useIsMobile";
import { cn } from "utils/cn";
import { packageStatus } from "utils/packageStatus";
import FilterModal from "components/filterModal";

// Estados de "Entrega fallida". La FSM (microservice-packages-status) sólo permite
// 6/7 desde 3 (En ruta) o 30 (En entrega); un Recolectado (1) el backend lo promueve
// a 3 antes de fallar. El resto de estados se rechaza por paquete.
const FAILED_STATUS_OPTIONS = [
  { value: 6, labelKey: "Dirección incorrecta o incompleta" },
  { value: 7, labelKey: "Sin moradores / rechazo del cliente" },
];

// Estados elegibles para el cambio masivo a fallido: En Ruta (3), En entrega (30) y
// Recolectado (1). Este último NO va directo a fallido en la FSM; el backend lo
// promueve a "En Ruta" y luego lo marca fallido.
const ELIGIBLE_STATES = [1, 3, 30];

// Cuántos paquetes se traen a la vista previa. El operador selecciona de aquí; si
// hay más, se le pide refinar filtros (evita seleccionar a ciegas miles de docs).
const PREVIEW_LIMIT = 200;
// Tamaño de cada request al backend. El backend admite hasta 500 por lote; troceamos
// más chico para no exceder timeouts en selecciones grandes.
const SUBMIT_CHUNK = 100;

const segBtn = (active) =>
  [
    "flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2 sm:py-1.5 min-h-[44px] sm:min-h-0",
    "text-sm font-medium whitespace-nowrap transition-colors",
    active ? "bg-bg text-brand-600 shadow-card" : "text-fg-muted hover:text-fg",
  ].join(" ");

const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

// Mapea la salida del modal "Filtrar y ordenar" a los args de getAdminPackages
// (GET /packages). Espejo del mapeo de resume.jsx para mantener paridad de filtros.
const mapFiltersToArgs = (f, page, limit) => {
  const dateStart = f?.dateStart
    ? moment(f.dateStart, "YYYY-MM-DD").format("MM/DD/YYYY")
    : undefined;
  // The backend already treats endDateCreation as inclusive (end of day), so no +1 day.
  const dateEnd = f?.dateEnd
    ? moment(f.dateEnd, "YYYY-MM-DD").format("MM/DD/YYYY")
    : undefined;

  return {
    page,
    limit,
    orderField: f?.orderBy || "updatedAt",
    sortType: "desc",
    coms: f?.coms,
    zipCode: f?.zipCode,
    status: f?.status,
    type: f?.type,
    startDate: dateStart,
    endDate: dateEnd,
    assignTo: f?.deliveryId,
    client: f?.clientId,
    meliStatusMismatch: f?.meliStatusMismatch || undefined,
  };
};

export default function BulkFailedDeliveryPanel() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isMobile = useIsMobile();

  const FAILED_STATUS = FAILED_STATUS_OPTIONS.map((o) => ({
    value: o.value,
    label: t(o.labelKey),
  }));

  // ── Filtros + vista previa ──
  const [filters, setFilters] = useState({});
  const [openFilters, setOpenFilters] = useState(false);
  const [packages, setPackages] = useState(null); // null = sin búsqueda aún
  const [totalDocs, setTotalDocs] = useState(0);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // ── Estado destino + formulario de fallo ──
  const [status, setStatus] = useState(6);
  const [deliveryConfig, setDeliveryConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [description, setDescription] = useState("");

  // ── Evidencia compartida ──
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState("");

  // ── Ubicación compartida (lat/lng) para todo el lote ──
  // La app envía la ubicación del repartidor por cada intento fallido; aquí un
  // mismo par se reutiliza para todos los paquetes, manteniendo consistencia.
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");

  // ── Envío / resultado ──
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState("");
  const [result, setResult] = useState(null);

  // Config del formulario de fallo (por operador, resuelto desde el JWT).
  useEffect(() => {
    let mounted = true;
    setLoadingConfig(true);
    getDeliveryConfig()
      .then((res) => {
        if (!mounted) return;
        setDeliveryConfig(res?.data || null);
      })
      .catch(() => {
        if (mounted) setDeliveryConfig(null);
      })
      .finally(() => {
        if (mounted) setLoadingConfig(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  // Libera el object URL de la miniatura al cambiar/desmontar.
  useEffect(() => {
    return () => {
      if (filePreview) URL.revokeObjectURL(filePreview);
    };
  }, [filePreview]);

  const fetchPackages = useCallback(
    async (f) => {
      setLoadingPackages(true);
      const res = await getAdminPackages(mapFiltersToArgs(f, 1, PREVIEW_LIMIT));
      setLoadingPackages(false);

      if (res?.error) {
        showToast(res.error?.message || t("No se pudieron cargar los paquetes"), false);
        return;
      }
      const docs = res?.data?.docs || [];
      setPackages(docs);
      setTotalDocs(res?.data?.totalDocs || docs.length);
      // Selección inicial: sólo los elegibles (en ruta / en entrega).
      setSelectedIds(new Set(docs.filter((p) => ELIGIBLE_STATES.includes(p.status)).map((p) => p._id)));
      setResult(null);
    },
    [t]
  );

  const handleApplyFilters = (outgoing) => {
    setFilters(outgoing);
    setOpenFilters(false);
    fetchPackages(outgoing);
  };

  const handleResetFilters = () => {
    setFilters({});
    setPackages(null);
    setSelectedIds(new Set());
    setResult(null);
  };

  const toggleSelection = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const eligiblePreview = useMemo(
    () => (packages || []).filter((p) => ELIGIBLE_STATES.includes(p.status)),
    [packages]
  );

  const selectAllEligible = () => {
    setSelectedIds(new Set(eligiblePreview.map((p) => p._id)));
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // El backend (bulk-failed-delivery.controller) solo acepta image/jpeg e
    // image/png: un webp/heic hacía subir el lote completo para fallar al final.
    if (f.type !== "image/jpeg" && f.type !== "image/png") {
      showToast(t("La foto debe ser JPG o PNG."), false);
      e.target.value = "";
      return;
    }
    if (filePreview) URL.revokeObjectURL(filePreview);
    setFile(f);
    setFilePreview(URL.createObjectURL(f));
  };

  const clearFile = () => {
    if (filePreview) URL.revokeObjectURL(filePreview);
    setFile(null);
    setFilePreview("");
  };

  // ── Formulario dinámico según deliveryConfig.failedDeliveryForm[status] ──
  const formEntry = useMemo(() => {
    const entries = deliveryConfig?.failedDeliveryForm || [];
    return entries.find((e) => e.status === status) || { status, mode: "none" };
  }, [deliveryConfig, status]);

  const showText = formEntry.mode === "text" || formEntry.mode === "options_text";
  const showOptions = formEntry.mode === "options" || formEntry.mode === "options_text";
  const textRequired = !!formEntry.text?.required;
  const textMin = formEntry.text?.minLength;
  const textMax = formEntry.text?.maxLength;
  const optionValues = useMemo(() => formEntry.options?.values || [], [formEntry]);

  const toggleOption = (value) => {
    setSelectedOptions((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  // Al cambiar de estado destino, limpia lo específico del formulario anterior.
  useEffect(() => {
    setSelectedOptions([]);
    setDescription("");
  }, [status]);

  // Coords opcionales: ambos vacíos es válido (no se envían); si se pone una, ambas
  // deben ser números en rango. Bloquea el envío si el par está a medio llenar.
  const coordsValid = useMemo(() => {
    const latEmpty = latitude.trim() === "";
    const lngEmpty = longitude.trim() === "";
    if (latEmpty && lngEmpty) return true;
    if (latEmpty || lngEmpty) return false;
    const lat = Number(latitude);
    const lng = Number(longitude);
    return (
      Number.isFinite(lat) &&
      Number.isFinite(lng) &&
      Math.abs(lat) <= 90 &&
      Math.abs(lng) <= 180
    );
  }, [latitude, longitude]);

  const buildFailedPayload = useCallback(() => {
    const payload = {};
    if (showOptions && selectedOptions.length) {
      payload.selectedOptions = selectedOptions;
      // `option` alimenta el failReason del evento; usa la primera selección.
      payload.option = selectedOptions[0];
    }
    if (showText && description.trim()) {
      payload.description = description.trim();
    }
    if (latitude.trim() !== "" && longitude.trim() !== "") {
      const lat = Number(latitude);
      const lng = Number(longitude);
      if (
        Number.isFinite(lat) &&
        Number.isFinite(lng) &&
        Math.abs(lat) <= 90 &&
        Math.abs(lng) <= 180
      ) {
        payload.coords = { latitude: lat, longitude: lng };
      }
    }
    return Object.keys(payload).length ? payload : undefined;
  }, [showOptions, showText, selectedOptions, description, latitude, longitude]);

  const formValid = useMemo(() => {
    if (showOptions && optionValues.length && selectedOptions.length === 0) return false;
    if (showText && textRequired) {
      const len = description.trim().length;
      if (len === 0) return false;
      if (textMin && len < textMin) return false;
    }
    if (textMax && description.length > textMax) return false;
    return true;
  }, [showOptions, optionValues, selectedOptions, showText, textRequired, textMin, textMax, description]);

  const canSubmit =
    selectedIds.size > 0 && !!file && formValid && coordsValid && !submitting;

  const reasonLabel = useCallback(
    (reason) => {
      if (typeof reason === "string" && reason.startsWith("invalid_state:")) {
        const s = reason.split(":")[1];
        return t("Estado no elegible ({{label}})", { label: packageStatus[s] || s });
      }
      const map = {
        not_found_or_forbidden: t("No encontrado o de otra empresa"),
        not_found: t("No encontrado"),
        forbidden: t("No autorizado"),
        invalid_request: t("Solicitud inválida"),
        transition_not_confirmed: t("Transición no confirmada (reintenta)"),
        internal_error: t("Error interno"),
      };
      return map[reason] || reason;
    },
    [t]
  );

  const handleConfirmSubmit = async () => {
    setSubmitting(true);
    setProgress("");
    // batchId estable para toda la acción: reintentar es idempotente por paquete.
    const batchId =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `batch-${moment().valueOf()}`;
    const failed = buildFailedPayload();
    const idChunks = chunk(Array.from(selectedIds), SUBMIT_CHUNK);
    const agg = { requested: 0, processed: 0, ok: [], rejected: [] };

    for (let i = 0; i < idChunks.length; i++) {
      setProgress(t("Lote {{current}}/{{total}}", { current: i + 1, total: idChunks.length }));
      // eslint-disable-next-line no-await-in-loop
      const res = await bulkFailedDelivery({ ids: idChunks[i], status, batchId, file, failed });
      if (!res.success) {
        setSubmitting(false);
        setProgress("");
        setResult(agg);
        setConfirmOpen(false);
        showToast(res.error?.message || t("Falló el envío del lote"), false);
        return;
      }
      agg.requested += res.data?.requested || 0;
      agg.processed += res.data?.processed || 0;
      agg.ok.push(...(res.data?.ok || []));
      agg.rejected.push(...(res.data?.rejected || []));
    }

    setSubmitting(false);
    setProgress("");
    setConfirmOpen(false);
    setResult(agg);
    setSelectedIds(new Set());
    showToast(
      t("{{count}} paquetes marcados como entrega fallida", { count: agg.ok.length }),
      true
    );
    // Refresca la vista para reflejar los nuevos estados.
    if (Object.keys(filters).length) fetchPackages(filters);
  };

  const selectedCount = selectedIds.size;
  const currentStatusLabel = FAILED_STATUS.find((o) => o.value === status)?.label;

  const fieldLabel = "text-sm font-semibold text-fg";
  const fieldHint = "text-xs text-fg-subtle";
  const groupTitle =
    "flex items-center gap-1.5 text-[0.72rem] font-bold uppercase tracking-wider text-fg-subtle";

  const receiverName = (p) =>
    [p?.receiver_name, p?.receiver_lastName].filter(Boolean).join(" ") ||
    p?.receiverName ||
    t("Sin nombre");

  return (
    <div className="w-full min-w-0 flex flex-col gap-5">
      {/* ── Aviso de acción destructiva ── */}
      <div className="rounded-xl border border-status-warning/30 bg-status-warning-soft px-4 py-3">
        <div className="flex items-start gap-2 text-sm text-status-warning">
          <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
          <p className="m-0">
            {t(
              "Marca varios paquetes como \"Entrega fallida\" reutilizando el mismo flujo que la app del repartidor. Sólo se afectan paquetes de tu empresa en estado \"Recolectado\", \"En ruta\" o \"En entrega\" (los Recolectados pasan primero a \"En ruta\"). Es una acción con efectos (evento, notificaciones y métricas): revisa la selección antes de confirmar."
            )}
          </p>
        </div>
      </div>

      {/* ── Barra de filtros ── */}
      <Card className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-fg-muted">
          <Filter className="w-4 h-4" />
          {Object.keys(filters).length
            ? t("Filtros aplicados. {{count}} paquetes encontrados.", { count: totalDocs })
            : t("Define filtros para buscar los paquetes a marcar.")}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleResetFilters}
            disabled={loadingPackages || !packages}
            iconLeft={<RotateCcw className="w-4 h-4" />}
          >
            {t("Limpiar")}
          </Button>
          <Button
            size="sm"
            onClick={() => setOpenFilters(true)}
            disabled={loadingPackages}
            iconLeft={<Search className="w-4 h-4" />}
          >
            {t("Filtrar y ordenar")}
          </Button>
        </div>
      </Card>

      {totalDocs > PREVIEW_LIMIT && (
        <div className="rounded-lg bg-status-info-soft px-4 py-2.5 text-sm text-status-info">
          {t(
            "Se muestran los primeros {{shown}} de {{total}}. Refina los filtros para acotar la selección.",
            { shown: PREVIEW_LIMIT, total: totalDocs }
          )}
        </div>
      )}

      {/* ── Grid: tabla | acción ── */}
      <div className="grid grid-cols-1 gap-5 items-start xl:grid-cols-[minmax(0,1fr)_320px]">
        {/* Tabla de vista previa */}
        <Card className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm font-semibold text-fg">
              {t("Paquetes")}{" "}
              {packages ? (
                <span className="text-fg-muted">
                  ({eligiblePreview.length} {t("elegibles")})
                </span>
              ) : null}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={selectAllEligible}
                disabled={!eligiblePreview.length}
              >
                {t("Seleccionar elegibles")}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedIds(new Set())}
                disabled={!selectedCount}
              >
                {t("Limpiar selección")}
              </Button>
            </div>
          </div>

          {loadingPackages ? (
            <div className="flex flex-col gap-2 py-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-11 w-full" />
              ))}
            </div>
          ) : !packages ? (
            <EmptyState
              icon={<SearchX />}
              title={t("Sin resultados aún")}
              description={t('Aplica filtros con "Filtrar y ordenar" para ver los paquetes.')}
            />
          ) : !packages.length ? (
            <EmptyState
              icon={<Inbox />}
              title={t("Sin paquetes")}
              description={t("Ningún paquete coincide con los filtros actuales.")}
            />
          ) : isMobile ? (
            <ul className="flex list-none flex-col gap-2.5 p-0 m-0">
              {packages.map((p) => {
                const eligible = ELIGIBLE_STATES.includes(p.status);
                const checked = selectedIds.has(p._id);
                return (
                  <li
                    key={p._id}
                    onClick={() => eligible && toggleSelection(p._id)}
                    className={cn(
                      "rounded-xl border bg-bg p-3 transition-colors",
                      eligible ? "cursor-pointer" : "opacity-60",
                      checked ? "border-brand-500 bg-brand-50" : "border-border"
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!eligible}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleSelection(p._id)}
                        className="mt-0.5 h-4 w-4 shrink-0 accent-brand-500"
                        aria-label={t("Seleccionar {{id}}", { id: p.followingNumber || p._id })}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono text-xs text-fg-muted">
                            {p.followingNumber || p.order_id || "—"}
                          </span>
                          <Badge tone={eligible ? "neutral" : "warning"}>
                            {t(packageStatus[p.status] || p.status)}
                          </Badge>
                        </div>
                        <p className="mt-1 truncate text-sm font-semibold text-fg">
                          {receiverName(p)}
                        </p>
                        <p className="mt-0.5 text-xs text-fg-muted">
                          {p.shipping_address?.municipality?.name || "—"}
                        </p>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH className="w-10" />
                  <TH>{t("Seguimiento")}</TH>
                  <TH>{t("Destinatario")}</TH>
                  <TH>{t("Comuna")}</TH>
                  <TH>{t("Estado")}</TH>
                </TR>
              </THead>
              <TBody>
                {packages.map((p) => {
                  const eligible = ELIGIBLE_STATES.includes(p.status);
                  const checked = selectedIds.has(p._id);
                  return (
                    <TR
                      key={p._id}
                      onClick={() => eligible && toggleSelection(p._id)}
                      className={cn(
                        eligible ? "cursor-pointer" : "opacity-60",
                        checked && "bg-brand-50 hover:bg-brand-50"
                      )}
                    >
                      <TD onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={!eligible}
                          onChange={() => toggleSelection(p._id)}
                          className="h-4 w-4 cursor-pointer accent-brand-500"
                          aria-label={t("Seleccionar {{id}}", { id: p.followingNumber || p._id })}
                        />
                      </TD>
                      <TD className="font-mono text-xs text-fg-muted">
                        {p.followingNumber || p.order_id || "—"}
                      </TD>
                      <TD>
                        <strong className="font-semibold text-fg">{receiverName(p)}</strong>
                      </TD>
                      <TD>{p.shipping_address?.municipality?.name || "—"}</TD>
                      <TD>
                        <Badge tone={eligible ? "neutral" : "warning"}>
                          {t(packageStatus[p.status] || p.status)}
                        </Badge>
                      </TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          )}
        </Card>

        {/* Acción */}
        <Card className="flex flex-col gap-4 xl:sticky xl:top-4">
          <h3 className="text-base font-semibold text-fg">{t("Cambiar a entrega fallida")}</h3>

          {/* Estado destino */}
          <div className="flex flex-col gap-1.5">
            <span className={groupTitle}>
              <PackageX className="w-4 h-4" />
              {t("Motivo (estado)")}
            </span>
            <div className="grid grid-cols-1 gap-1 rounded-2xl bg-bg-muted p-1">
              {FAILED_STATUS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  aria-pressed={status === o.value}
                  className={segBtn(status === o.value)}
                  onClick={() => setStatus(o.value)}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {/* Formulario dinámico */}
          {loadingConfig ? (
            <Skeleton className="h-16 w-full" />
          ) : (
            <>
              {showOptions && (
                <div className="flex flex-col gap-1.5">
                  <span className={fieldLabel}>
                    {t("Opciones")}
                    {optionValues.length ? <span className="text-status-error"> *</span> : null}
                  </span>
                  {optionValues.length ? (
                    <div className="flex flex-col gap-1.5">
                      {optionValues.map((opt) => (
                        <label
                          key={opt}
                          className={cn(
                            "flex cursor-pointer items-center gap-2.5 rounded-lg border p-2.5 text-sm transition-colors",
                            selectedOptions.includes(opt)
                              ? "border-brand-500 bg-brand-50 text-brand-700"
                              : "border-border bg-bg-muted text-fg"
                          )}
                        >
                          <input
                            type="checkbox"
                            checked={selectedOptions.includes(opt)}
                            onChange={() => toggleOption(opt)}
                            className="shrink-0 accent-brand-500"
                          />
                          {opt}
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="m-0 text-xs text-fg-subtle">
                      {t("Tu empresa no configuró opciones para este estado.")}
                    </p>
                  )}
                </div>
              )}

              {showText && (
                <label className="flex flex-col gap-1.5">
                  <span className={fieldLabel}>
                    {t("Descripción")}
                    {textRequired ? <span className="text-status-error"> *</span> : null}
                  </span>
                  <textarea
                    rows={3}
                    value={description}
                    maxLength={textMax || 1000}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={t("Detalle del intento fallido")}
                    className="w-full resize-y rounded-lg border border-border bg-bg px-3 py-2 text-sm text-fg placeholder:text-fg-subtle focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                  <small className={fieldHint}>
                    {description.length}
                    {textMax ? `/${textMax}` : ""} {t("caracteres")}
                    {textMin ? ` · ${t("mínimo {{n}}", { n: textMin })}` : ""}
                  </small>
                </label>
              )}

              {formEntry.mode === "none" && (
                <p className="m-0 text-xs text-fg-subtle">
                  {t("Tu empresa no pide datos adicionales para este estado.")}
                </p>
              )}
            </>
          )}

          {/* Evidencia compartida */}
          <div className="flex flex-col gap-1.5">
            <span className={fieldLabel}>
              {t("Foto de evidencia (compartida)")} <span className="text-status-error">*</span>
            </span>
            {filePreview ? (
              <div className="relative w-full">
                <img
                  src={filePreview}
                  alt={t("Evidencia")}
                  className="w-full max-h-40 rounded-lg border border-border object-cover"
                />
                <button
                  type="button"
                  onClick={clearFile}
                  className="absolute right-2 top-2 rounded-full bg-bg/90 p-1 text-fg-muted shadow-card hover:text-fg"
                  aria-label={t("Quitar foto")}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className="flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed border-border bg-bg-muted px-3 py-5 text-center text-sm text-fg-muted transition-colors hover:border-brand-500 hover:bg-brand-50">
                <Camera className="w-6 h-6" />
                {t("Subir una foto")}
                <input type="file" accept="image/jpeg,image/png" onChange={handleFileChange} className="hidden" />
              </label>
            )}
            <small className={fieldHint}>
              {t("La misma foto se registra como evidencia para cada paquete del lote.")}
            </small>
          </div>

          {/* Ubicación compartida (lat/lng) para todo el lote */}
          <div className="flex flex-col gap-1.5">
            <span className={fieldLabel}>{t("Ubicación (lat/lng)")}</span>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="number"
                step="any"
                inputMode="decimal"
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
                placeholder={t("Latitud")}
                aria-label={t("Latitud")}
                className="w-full rounded-lg border border-border bg-bg px-3 py-2 text-sm text-fg placeholder:text-fg-subtle focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
              <input
                type="number"
                step="any"
                inputMode="decimal"
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
                placeholder={t("Longitud")}
                aria-label={t("Longitud")}
                className="w-full rounded-lg border border-border bg-bg px-3 py-2 text-sm text-fg placeholder:text-fg-subtle focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
            </div>
            {!coordsValid && (
              <small className="text-xs text-status-error">
                {t("Completa ambas coordenadas con valores válidos (lat −90 a 90, lng −180 a 180).")}
              </small>
            )}
            <small className={fieldHint}>
              {t("Opcional. La misma ubicación se registra para cada paquete, igual que la que envía la app del repartidor.")}
            </small>
          </div>

          <Button
            fullWidth
            disabled={!canSubmit}
            onClick={() => setConfirmOpen(true)}
            iconLeft={<Send className="w-4 h-4" />}
          >
            {t("Aplicar a {{count}} paquetes", { count: selectedCount })}
          </Button>
          {!file && selectedCount > 0 && (
            <p className="m-0 text-center text-xs text-status-error">
              {t("Sube una foto de evidencia para continuar.")}
            </p>
          )}
        </Card>
      </div>

      {/* ── Resultado ── */}
      {result && (
        <Card className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-status-success">
              <CheckCircle2 className="w-4 h-4" />
              {result.ok.length} {t("marcados")}
            </span>
            {result.rejected.length > 0 && (
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-status-error">
                <XCircle className="w-4 h-4" />
                {result.rejected.length} {t("rechazados")}
              </span>
            )}
          </div>

          {result.rejected.length > 0 && (
            <ul className="m-0 flex list-none flex-col p-0">
              {result.rejected.slice(0, 50).map((r) => (
                <li
                  key={r.id}
                  className="flex items-center justify-between gap-3 border-b border-border py-2 text-sm last:border-b-0"
                >
                  <span className="font-mono text-xs text-fg-muted">{r.id}</span>
                  <span className="text-status-error">{reasonLabel(r.reason)}</span>
                </li>
              ))}
              {result.rejected.length > 50 && (
                <li className="py-2 text-xs italic text-fg-subtle">
                  +{result.rejected.length - 50} {t("más...")}
                </li>
              )}
            </ul>
          )}
        </Card>
      )}

      {/* ── Modal de filtros (reutiliza el mismo de la lista de paquetes) ── */}
      <FilterModal
        open={openFilters}
        activeFilters={filters}
        callback={handleApplyFilters}
        closeModal={setOpenFilters}
        statusDisabled={false}
        user={user}
      />

      {/* ── Confirmación ── */}
      <Modal
        open={confirmOpen}
        onClose={() => !submitting && setConfirmOpen(false)}
        title={t("Confirmar cambio masivo")}
        description={t("Esta acción tiene efectos operativos y no es un simple cambio de estado.")}
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={submitting}>
              {t("Cancelar")}
            </Button>
            <Button onClick={handleConfirmSubmit} loading={submitting} disabled={submitting}>
              {submitting ? progress || t("Aplicando...") : t("Confirmar")}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-2 text-sm text-fg">
          <p className="m-0">
            {t("Vas a marcar")} <strong>{selectedCount}</strong>{" "}
            {t("paquetes como")} <strong>{currentStatusLabel}</strong>.
          </p>
          <p className="m-0 text-fg-muted">
            {t(
              "Se registrará el intento, se aplicará la transición de estado y se emitirán los eventos y notificaciones correspondientes por cada paquete elegible."
            )}
          </p>
          {progress && (
            <p className="m-0 text-status-info" role="status" aria-live="polite">
              {progress}
            </p>
          )}
        </div>
      </Modal>
    </div>
  );
}
